import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { DealTabs } from '@makemydeal/dr-shared-types';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, MAX_RESIDUALIZED_MSRP } from '../../../constants';
import { dealLockHooks } from '../../../hooks';
import { useFieldOverrideIndicator } from '../../../hooks/useFieldOverrideIndicator';
import { usePaymentCall } from '../../../utils/usePaymentCall';

const MRM_OVERRIDE_INDICATOR_TEST_ID = 'mrm-override-indicator';

export const useMaxResidualizedMsrpField = ({ tab }: { tab?: DealTabs }) => {
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const {
        enabled: overrideIndicatorEnabled,
        show: showOverrideIndicator,
        message: overrideMessage,
        recordManualOverride
    } = useFieldOverrideIndicator('maxResidualizedMsrp');
    const { value, handleChange, handleBlur, handleKeyDown } = usePaymentCall(
        offerSelectors.getMaxResidualizedMsrpOverrideWithFallback,
        offerActionCreators.updateMaxResidualizedMsrpOverride,
        {
            formatValueToString: (amount: any) => formatNumberAndCents(amount),
            onUserEdit: recordManualOverride,
            holdWhileTermResponsePending: true
        }
    );

    const fieldName = kebabCase(MAX_RESIDUALIZED_MSRP);
    const failed = useSelector(deskingSelectors.wasTheLastFailure(offerActionTypes.UPDATED_MAX_RESIDUALIZED_MSRP_OVERRIDE));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && tab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(tab, fieldName, failed));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failed, tab]);

    return {
        label: MAX_RESIDUALIZED_MSRP,
        overrideIndicatorProps: {
            enabled: overrideIndicatorEnabled,
            show: showOverrideIndicator,
            message: overrideMessage,
            testId: MRM_OVERRIDE_INDICATOR_TEST_ID
        },
        inputProps: {
            name: fieldName,
            onChange: handleChange,
            onBlur: handleBlur,
            placeholder: '0.00',
            hasError: failed,
            errorMessage: failedMessage,
            value,
            'data-testid': fieldName,
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            disabled: isLocked,
            inputPrefix: '$',
            autoInsertCommas: true,
            size: 'sm' as const,
            onKeyDown: handleKeyDown
        }
    };
};
